import {
  Annotation,
  AnnotationType,
  LinkAnnotation,
  Point
} from 'types/building'
import { Direction, KrpanoInterface, Stairway } from 'types/krpano'

const Krpano = class Krpano {
  static AddDirectionArrow = (
    krpano: KrpanoInterface,
    directions: Direction[]
  ) => {
    directions.forEach((direction) => {
      krpano.call(
        `add_direction(${direction.id},${direction.h},${direction.v},${direction.target},${direction.point_to})`
      )
    })
  }

  static IsAnnotationDataEmpty = (anno: Annotation | undefined): boolean => {
    if (!anno) return true

    // 1. Check if files array is empty
    const hasFiles = !!anno.files?.length

    // 2. Check for link formats (including BCP singular and plural fields)
    const hasLink =
      !!anno.link?.trim() ||
      !!anno.link_occurrence?.trim() ||
      !!anno.link_recovery?.trim() ||
      !!(
        anno.links && anno.links.some((l: LinkAnnotation) => !!l.link?.trim())
      ) ||
      !!(
        anno.link_occurrences &&
        anno.link_occurrences.some(
          (l: { link_occurrence?: string }) => !!l.link_occurrence?.trim()
        )
      ) ||
      !!(
        anno.link_recoveries &&
        anno.link_recoveries.some(
          (l: { link_recovery?: string }) => !!l.link_recovery?.trim()
        )
      )

    // 3. Check memo content
    // Requires validation against pure DOM elements containing only images, spacing, etc.
    const memoValues = [
      anno.memo,
      anno.memo_occurence,
      anno.memo_recovery,
      anno.memo_content,
      anno.memo_file_name,
      anno.memo_occurence_file_name,
      anno.memo_recovery_file_name
    ].filter(Boolean) as string[]
    const hasMemo = memoValues.some((m) => !Krpano.IsMemoEmptyCheckImage(m))

    // A unified rule for both Set Annotation and Normal Annotation:
    // They are only considered to have data if they have files, links, or memos.
    // The 'name' field is strictly a text parameter and does not count as attachable data.
    return !hasFiles && !hasLink && !hasMemo
  }

  static AddAnnotation = async (
    krpano: KrpanoInterface,
    annotations: Annotation[],
    isDisplay?: string | null,
    isTourVersion?: boolean,
    isShowEmptyMark: boolean = true
  ) => {
    const annotationPromises = annotations.map(async (annotation) => {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      const {
        id,
        head_h: headH,
        head_v: headV,
        mid_h: midH,
        mid_v: midV,
        h,
        v,
        type,
        memo,
        set_annotation_name: setAnnotationName,
        name
      } = annotation

      const annotationType = Object.values(AnnotationType).includes(type || '')
        ? type
        : AnnotationType.TRADITION

      krpano.call(`remove_annotation(${id})`)
      krpano.call(`remove_callout_memo_annotation(${id})`)
      // Tour version use call out plugin for show memo anotation
      if (annotationType === AnnotationType.MEMO && isTourVersion) {
        krpano.call(`removehotspot(empty-${id})`)
        const textContent = await this.FetchMemoContent(memo || '')
        const qlTextcontent = `<div class="ql-editor tour-memo p-0">${textContent}</div>`
        // replace " to ' if not replace plugin can't render this html
        const safeHtml = qlTextcontent.replace(/"/g, "'").replace(/,/g, '&#44;')

        krpano.call(
          `callwhen(loadcomplete, create-callout-annotation(${id},${h},${v},${safeHtml},${
            isDisplay || 'true'
          });)`
        )
      } else if (
        annotationType === AnnotationType.IMAGE ||
        annotationType === AnnotationType.MOVIE ||
        annotationType === AnnotationType.INFO
      ) {
        krpano.call(`removehotspot(empty-${id})`)
        krpano.call(
          `add_new_tour_annotation(${id},${h},${v},${annotationType}, 'true');`
        )
      } else {
        const isDataEmpty = Krpano.IsAnnotationDataEmpty(annotation)

        if (isDataEmpty && !isShowEmptyMark) {
          krpano.call(`removehotspot('empty-${id}')`)
          return
        }

        const displayText = setAnnotationName || name || ''

        const textContent = await this.FetchMemoContent(memo || '')

        try {
          const imgSrcs = Array.from(
            textContent.matchAll(/<img[^>]+src=["']([^"']+)["']/g)
          ).map((m) => m[1])
          await Promise.all(
            imgSrcs.map(
              (src) =>
                new Promise<void>((resolve) => {
                  const img = new Image()
                  img.onload = () => resolve()
                  img.onerror = () => resolve()
                  img.src = src
                })
            )
          )
        } catch {
          // ignore
        }

        const qlTextcontent = `<div class="ql-editor tour-memo p-0">${textContent}</div>`
        const safeHtml = qlTextcontent.replace(/"/g, "'").replace(/,/g, '&#44;')

        krpano.call(
          `add_annotation(${id},${headH},${headV},${midH},${midV},${h},${v},${annotationType},${safeHtml},${
            annotation.set_annotation_id ?? ''
          }, ${isDisplay || 'true'},${displayText});`
        )

        if (isDataEmpty && isShowEmptyMark) {
          krpano.call(`
            addhotspot('empty-${id}');
            set(hotspot['empty-${id}'].ath, ${h});
            set(hotspot['empty-${id}'].atv, ${v});
            hotspot['empty-${id}'].loadstyle('empty_mark_style');
            set(hotspot['empty-${id}'].visible, ${isDisplay || 'true'});
          `)
        } else {
          krpano.call(`removehotspot('empty-${id}')`)
        }
      }
    })

    await Promise.all(annotationPromises)
  }

  static AddTripodLogo = (
    krpano: KrpanoInterface,
    tripodUrl: string | undefined
  ) => {
    if (tripodUrl) {
      krpano.call(
        `callwhen(loadcomplete, addhotspot('tripod'); hotspot['tripod'].loadstyle('tripod-style'); set(hotspot['tripod'].url, '${tripodUrl}'); set(hotspot['tripod'].keep, 'true'); set(hotspot['tripod'].ath, '0.000'); set(hotspot['tripod'].atv, '90.000'); set(hotspot['tripod'].rotatewithview, 'false'); set(hotspot['tripod'].handcursor, 'false'); set(hotspot['tripod'].enabled, 'false');)`
      )
    } else {
      krpano.call("removehotspot('tripod');")
    }
  }

  static UnmountPanoFull = () => {
    const krpano = document.getElementById(
      'embedpano-full'
    ) as unknown as KrpanoInterface
    if (krpano) {
      window.removepano('embedpano-full')
    }
  }

  static UnmountPanoPreview = () => {
    const krpano = document.getElementById(
      'embedpano-preview'
    ) as unknown as KrpanoInterface
    if (krpano) {
      window.removepano('embedpano-preview')
    }
  }

  static UnmountPanoViewer = () => {
    const krpano = document.getElementById(
      'embedpano-viewer'
    ) as unknown as KrpanoInterface
    if (krpano) {
      window.removepano('embedpano-viewer')
    }
  }

  static RemoveTempAnnotation = () => {
    const krpano = document.getElementById(
      'embedpano-full'
    ) as unknown as KrpanoInterface
    krpano?.call('hide_temp_hotspot();')
  }

  static UnmountFull = () => {
    this.UnmountPanoFull()
    this.UnmountPanoPreview()
  }

  static GetAngle = (dx: number, dy: number) => {
    const d = (Math.atan2(dy, dx) * 180) / Math.PI
    if (d < 0) {
      return d + 360
    }
    return d
  }

  static reConfigAngleOver360 = (angle: number) => {
    if (angle > 360) {
      return angle - 360
    }
    return angle
  }

  static TogglePointLock = (isLocked?: boolean) => {
    const krpano = document.getElementById(
      'embedpano-full'
    ) as unknown as KrpanoInterface
    krpano?.call(`set(islocked,${isLocked || false});`)
  }

  static LoadChecking = (callback: () => void) => {
    const fullpanoLoading = (
      document.getElementById('embedpano-full') as unknown as KrpanoInterface
    )?.get('loadcomplete')

    const previewpanoLoading = (
      document.getElementById('embedpano-preview') as unknown as KrpanoInterface
    )?.get('loadcomplete')
    if (
      (fullpanoLoading === undefined && previewpanoLoading === undefined) ||
      (fullpanoLoading === 'true' && previewpanoLoading === 'true')
    ) {
      callback()
    }
  }

  static LoadCheckingViewerMode = (callback: () => void) => {
    const fullpanoLoading = (
      document.getElementById('embedpano-viewer') as unknown as KrpanoInterface
    )?.get('loadcomplete')

    if (fullpanoLoading || fullpanoLoading === undefined) {
      callback()
    }
  }

  static NormalizeHGap = (hGap: number): number => {
    if (hGap >= 360) {
      return this.NormalizeHGap(hGap - 360)
    }
    if (hGap < 0) {
      return this.NormalizeHGap(hGap + 360)
    }
    return hGap
  }

  static FormatStairName = (name?: string) => {
    const chars: Record<string, string> = {
      "'": '&apos;',
      '"': '&quot;'
    }
    return (
      name
        ? `<p>フロア：${
            name.length > 10 ? `${name?.slice(0, 10)}...` : name
          }</p>`
        : ''
    ).replace(/['"]/g, (m) => chars[m])
  }

  static AddStairway = (
    krpano: KrpanoInterface | null,
    stairways: Stairway[]
  ) => {
    if (krpano) {
      stairways.forEach((item) => {
        const newTooltip = this.FormatStairName(item.nextFloorName)

        krpano.call(
          `add_stairway(${item.id}, ${item.h}, ${item.v}, ${item.nextFloorId}, ${item.nextPointId}, ${newTooltip}, ${item.areaId})`
        )
      })
    }
  }

  static RevalidateDirection = (directions: Direction[]) => {
    const krpano = document.getElementById(
      'embedpano-full'
    ) as unknown as KrpanoInterface

    // const addingHgap = hgap > 180 ? 360 - hgap : hgap
    directions.forEach((item) => {
      // if (!item.dHgap) {
      krpano.call(`set(hotspot[arrow-${item.id}].ath, ${item.h});`)
      krpano.call(`set(hotspot[arrow-tooltip-${item.id}].ath, ${item.h});`)
      // }
    })
  }

  static FetchMemoContent = async (s3Link: string) => {
    // is valid digitori360 s3 link
    if (s3Link.startsWith('https://digitori360')) {
      const response = await fetch(s3Link)
      const blob = await response.blob()
      const textContent = await blob.text()
      return textContent
    }
    // For non-S3 links, just return the content as is
    return s3Link
  }

  static IsMemoEmpty(value: string) {
    if (!value) return true

    // Fast path: if there is an image, it is never empty.
    // This avoids running heavy regex on Base64 image strings.
    if (value.includes('<img')) {
      return false
    }

    // Use non-capturing, non-backtracking regex to strip HTML
    return value.replace(/<[^>]*>/g, '').trim().length === 0
  }

  static IsMemoEmptyCheckImage(value: string) {
    if (!value) return true

    if (value.includes('<img')) {
      return false
    }

    const textOnly = value.replace(/<[^>]*>/g, '').trim()
    return textOnly.length === 0
  }

  static GetMemoKeyFromPresignedUrl(presignedUrl: string) {
    const startInd = presignedUrl.indexOf('/assets')
    const endIdx = presignedUrl.indexOf('.txt')
    const key = presignedUrl.substring(startInd, endIdx + 4)
    return key
  }

  static ToggleDisplayHotspot(
    krpano: KrpanoInterface | null,
    isDisplay: boolean
  ) {
    krpano?.call(`show_all_hotspot(${isDisplay})`)
  }

  static escapeXml(unsafe: string): string {
    if (!unsafe) return ''
    return unsafe
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&apos;')
  }

  /**
   * Generates a single scene XML string for Krpano.
   * Updated: fov set to 120 for a wider default view, fovmax to 160 for extra zoom-out capability.
   */
  static generateSceneXML({
    sceneName,
    tilesUrl,
    multires,
    s3Path,
    thumbnailUrl
  }: {
    sceneName?: string
    tilesUrl?: string
    multires?: string
    s3Path: string
    thumbnailUrl: string
  }) {
    const xmlString = multires
      ? `
          <krpano>
              <scene name="${sceneName}">
                  <view
                      hlookat="0"
                      vlookat="0"
                      distortion="0.0"
                      fov="100"
                      limitview="auto"
                      fovmin="50"
                      fovmax="160"
                      fisheye="0.0"
                      stereographic="false"
                      cubestrip="true"
                      smooth="true"
                  />
                  <preview url="${this.escapeXml(
                    tilesUrl ? `${tilesUrl}preview.jpg` : ''
                  )}" />
                  <image>
                      <cube url="${this.escapeXml(
                        tilesUrl || ''
                      )}%s/l%l/%v/l%l_%s_%v_%h.jpg" multires="${multires}" />
                  </image>
              </scene>
          </krpano>`.trim()
      : `
         <krpano>
                <scene name="${sceneName}">
                  <view hlookat="0" vlookat="0" distortion="0.0" fov="100" limitview="auto" fovmin="50" fovmax="160" />
                  <preview url="${this.escapeXml(thumbnailUrl)}" />
                  <image>
                    <sphere url="${this.escapeXml(s3Path)}" />
                  </image>
                </scene>
            </krpano>
            `.trim()

    return xmlString
  }

  /**
   * Generates multiple scenes XML string (used for preloading).
   * Updated FOV settings to match the primary scene generator.
   */
  static generateSceneXMLs(
    data: {
      id: number
      tilesUrl?: string
      multires?: string
      s3Path: string
      thumbnailUrl: string
    }[]
  ) {
    let sceneXml = '<krpano>'
    data.forEach((item) => {
      const { id, tilesUrl, multires, s3Path, thumbnailUrl } = item
      sceneXml += multires
        ? `
          <scene name="scene_${id}">
              <view
                  hlookat="0"
                  vlookat="0"
                  distortion="0.0"
                  fov="100"
                  fovmin="50"
                  fovmax="160"
                  fisheye="0.0"
                  stereographic="false"
                  cubestrip="true"
                  smooth="true"
                  limitview="auto"
              />
              <preview url="${this.escapeXml(
                tilesUrl ? `${tilesUrl}preview.jpg` : ''
              )}" />
              <image>
                  <cube url="${this.escapeXml(
                    tilesUrl || ''
                  )}%s/l%l/%v/l%l_%s_%v_%h.jpg" multires="${multires}" />
              </image>
          </scene>`
        : `<scene name="scene_${id}">
              <view hlookat="0" vlookat="0" distortion="0.0" fov="100" fovmin="50" fovmax="160" limitview="auto"/>
              <preview  url="${this.escapeXml(thumbnailUrl)}" />
              <image>
                <sphere url="${this.escapeXml(s3Path)}" />
              </image>
            </scene>
        `
    })
    sceneXml += '</krpano>'.trim()

    return sceneXml
  }

  static getScenceNameByFileId = (
    currentSelectedPoint: string | undefined,
    points: Point[],
    selectedSubImage: number | undefined
  ) => {
    if (!currentSelectedPoint) return 'scene_0'

    if (selectedSubImage) {
      return `scene_${selectedSubImage}`
    }

    const point = points?.find((point) => point.id === currentSelectedPoint)
    return point ? `scene_${point?.image360?.id}` : 'scene_0'
  }

  static loadViewDirection = (
    krpano: KrpanoInterface,
    h: number,
    v: number,
    z: number,
    currentsence: string
  ) => {
    krpano.call(`load_view_direction(${h}, ${v}, ${z},${currentsence})`)
  }

  static NormalizeAngle = (angle: number): number => {
    let normalized = angle % 360
    if (normalized > 180) {
      normalized -= 360
    } else if (normalized < -180) {
      normalized += 360
    }
    return normalized
  }

  static GetFrontYaw = (
    point: Point | undefined,
    imageId: number | string | undefined | null
  ): number => {
    if (!point || !imageId) return 0
    if (point.image360?.id === Number(imageId)) {
      return point.initView?.h ?? 0
    }
    const subImg = point.subImages?.find((img) => img.id === Number(imageId))
    return subImg?.initView?.h ?? 0
  }
}

export default Krpano
